import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowRight, CheckCircle2, Download, FileUp, Loader2, PenLine, Play, RefreshCw, Replace,
} from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import {
  applyRenumberingBatch, downloadRenumberingExport, fetchRenumberingBatch, fetchRenumberingBatches,
  simulateRenumberingBatch, uploadRenumberingBatch, type RenumberingFilter,
} from '@/hooks/useRenumbering';
import type {
  RenumberingAddressPart, RenumberingBatchDetail, RenumberingBatchDto, RenumberingLineDto, RenumberingVerdict,
} from '@uniticket/shared';

// Écran « Renumbering » (H-1) — un lot Proximus, du dépôt à l'application. ADMINS SEULEMENT.
//
// ⛔ L'ÉCRITURE NE VA QUE DANS `address_mapping_table`, jamais dans public.address (règle d'Anouk du 31-08 :
// même JVID, ni création ni suppression). Elle n'existe qu'APRÈS une simulation lue, et c'est un second
// geste, confirmé sur un récapitulatif chiffré. Le serveur rejoue la résolution avant d'écrire : si le DMP a
// bougé, rien ne part et le rapport est refait.
//
// Libellés en français, comme les autres écrans d'administration (registre JVID, journal d'actions) :
// ces pages ne sortent pas de l'équipe interne.

const VERDICT_STYLE: Record<RenumberingVerdict, { label: string; pill: string; help: string }> = {
  RESOLVED: {
    label: 'Résolue',
    pill: 'bg-emerald-100 text-emerald-700',
    help: 'Un seul logement trouvé, sans conflit : la ligne partira à l\'application.',
  },
  ALREADY_DONE: {
    label: 'Déjà faite',
    pill: 'bg-sky-100 text-sky-700',
    help: 'Proximus connaît déjà cette adresse : le lot a probablement déjà été appliqué. Rien à écrire.',
  },
  AMBIGUOUS: {
    label: 'À trancher',
    pill: 'bg-amber-100 text-amber-700',
    help: 'Plusieurs logements possibles, un chevauchement à confirmer, ou une adresse cible déjà prise. Rien ne part.',
  },
  UNRESOLVED: {
    label: 'Introuvable',
    pill: 'bg-rose-100 text-rose-700',
    help: 'Ni le DMP ni les déclarations Proximus ne portent cette rue et ce numéro sous ce code postal.',
  },
  UNIT_MISSING: {
    label: 'Boîte absente',
    pill: 'bg-violet-100 text-violet-700',
    help: 'Le numéro existe, la boîte demandée non — la divergence à trancher, jamais une création.',
  },
};

const PENDING_STYLE = { label: 'Non simulée', pill: 'bg-slate-100 text-slate-500' };

const STATUS_LABEL: Record<RenumberingBatchDto['status'], string> = {
  DRAFT: 'Déposé',
  SIMULATED: 'Simulé',
  APPLYING: 'En cours d\'écriture',
  APPLIED: 'Appliqué',
};

type Filter = RenumberingFilter | null;

const COUNTERS: { key: Exclude<RenumberingFilter, 'APPLY_FAILED'>; count: keyof RenumberingBatchDto['counts'] }[] = [
  { key: 'RESOLVED', count: 'resolved' },
  { key: 'ALREADY_DONE', count: 'alreadyDone' },
  { key: 'AMBIGUOUS', count: 'ambiguous' },
  { key: 'UNIT_MISSING', count: 'unitMissing' },
  { key: 'UNRESOLVED', count: 'unresolved' },
  { key: 'PENDING', count: 'pending' },
];

const fmt = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** « Rue Paul Pastur 2 A001 » — l'extension du DMP porte souvent le numéro complet, on ne la répète pas. */
function addressText(a: RenumberingAddressPart): string {
  const number = a.houseNumberExtension && a.houseNumberExtension !== a.houseNumber
    ? a.houseNumberExtension
    : a.houseNumber;
  const line = [a.streetName, number].filter(Boolean).join(' ');
  const box = a.busNumber?.trim() ? ` bte ${a.busNumber.trim()}` : '';
  return (line + box).trim() || '—';
}

/** Ce que le FICHIER demande, quand aucun logement n'a été retenu : ce qui n'y est pas ne bouge pas. La boîte
 *  suit la règle du serveur — colonne présente mais vide = plus de boîte (`??`, pas `||`). */
function requestedText(line: RenumberingLineDto): string {
  const merged: RenumberingAddressPart = {
    streetName: line.after.streetName || line.before.streetName,
    houseNumber: line.after.houseNumber || line.before.houseNumber,
    houseNumberExtension: line.after.houseNumberExtension || line.before.houseNumberExtension,
    busNumber: line.after.busNumber ?? line.before.busNumber,
    postalCode: line.after.postalCode || line.before.postalCode,
    city: line.after.city || line.before.city,
  };
  return addressText(merged);
}

function exportFilename(batch: RenumberingBatchDto): string {
  const base = batch.filename.replace(/\.[^.]+$/, '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'lot';
  return `renumbering-${base}-a-renvoyer.csv`;
}

function VerdictPill({ verdict }: { verdict: RenumberingVerdict | null }) {
  const style = verdict ? VERDICT_STYLE[verdict] : PENDING_STYLE;
  return (
    <span
      className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', style.pill)}
      title={verdict ? VERDICT_STYLE[verdict].help : 'Déposée, pas encore résolue contre le DMP.'}
    >
      {style.label}
    </span>
  );
}

export function RenumberingPage() {
  const [batches, setBatches] = useState<RenumberingBatchDto[] | null>(null);
  const [detail, setDetail] = useState<RenumberingBatchDetail | null>(null);
  const [filter, setFilter] = useState<Filter>(null);
  const [busy, setBusy] = useState<'upload' | 'simulate' | 'apply' | 'export' | 'detail' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const confirmDmpWrite = useDmpWriteConfirm();

  const loadBatches = useCallback(async () => {
    try {
      setBatches((await fetchRenumberingBatches()).data);
    } catch (err) {
      toast.error((err as Error)?.message || 'Impossible de lire les lots.');
    }
  }, []);

  useEffect(() => { void loadBatches(); }, [loadBatches]);

  const openBatch = useCallback(async (id: string, next: Filter) => {
    setBusy('detail');
    try {
      const res = await fetchRenumberingBatch(id, next ?? undefined);
      setDetail(res.data);
      setFilter(next);
    } catch (err) {
      toast.error((err as Error)?.message || 'Impossible de lire ce lot.');
    } finally {
      setBusy(null);
    }
  }, []);

  async function onUpload(file: File) {
    setBusy('upload');
    try {
      const res = await uploadRenumberingBatch(file);
      toast.success(`${res.data.rowCount} ligne(s) lue(s) dans « ${res.data.filename} ».`);
      setConfirming(false);
      await loadBatches();
      await openBatch(res.data.id, null);
    } catch (err) {
      // Le serveur nomme les colonnes qu'il a lues : c'est le message qui permet de corriger le fichier.
      toast.error((err as Error)?.message || 'Le fichier n\'a pas pu être lu.', { duration: 12000 });
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function onSimulate(id: string) {
    setBusy('simulate');
    setConfirming(false);
    try {
      const res = await simulateRenumberingBatch(id);
      setDetail(res.data);
      setFilter(null);
      await loadBatches();
      const c = res.data.batch.counts;
      toast.success(`Simulation : ${c.resolved} résolue(s), ${c.alreadyDone} déjà faite(s), ${c.ambiguous + c.unresolved + c.unitMissing} à trancher.`);
    } catch (err) {
      toast.error((err as Error)?.message || 'La simulation a échoué.');
    } finally {
      setBusy(null);
    }
  }

  async function onApply(batch: RenumberingBatchDto) {
    if (!batch.simulatedAt) return;
    if (!confirmDmpWrite(`écrire ${batch.counts.resolved} déclaration(s) Proximus dans la mapping table`)) return;
    setBusy('apply');
    try {
      const res = await applyRenumberingBatch(batch.id, batch.simulatedAt);
      setDetail(res.data.detail);
      setFilter(null);
      setConfirming(false);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.written} ligne(s) écrite(s), ${res.data.failed} en échec — voir le filtre « Écriture en échec ».`, { duration: 12000 });
      } else {
        toast.success(`${res.data.written} déclaration(s) Proximus écrite(s) et relue(s) dans la mapping table.`);
      }
    } catch (err) {
      // 409 « le DMP a bougé » : le serveur a refait le rapport — on recharge pour le montrer tel quel.
      toast.error((err as Error)?.message || 'L\'application a échoué.', { duration: 15000 });
      setConfirming(false);
      await openBatch(batch.id, null);
    } finally {
      setBusy(null);
      await loadBatches();
    }
  }

  async function onExport(batch: RenumberingBatchDto) {
    setBusy('export');
    try {
      await downloadRenumberingExport(batch.id, exportFilename(batch));
    } catch (err) {
      toast.error((err as Error)?.message || 'Export CSV impossible.');
    } finally {
      setBusy(null);
    }
  }

  const batch = detail?.batch ?? null;
  // Le récapitulatif se calcule sur TOUTES les lignes résolues : il n'est proposé que sur la vue non filtrée.
  const resolvedLines = detail && filter === null ? detail.lines.filter((l) => l.verdict === 'RESOLVED') : [];
  const overwrites = resolvedLines.filter((l) => l.declared).length;
  const canApply = batch?.status === 'SIMULATED' && batch.counts.resolved > 0;
  const toSendBack = batch ? batch.counts.unitMissing + batch.counts.unresolved : 0;
  const missingUnits = detail?.warnings.reduce((n, w) => n + w.missing.length, 0) ?? 0;
  // Même délai que le serveur (APPLYING_STALE_MS) : une application interrompue ne bloque pas le lot à vie.
  const applyingNow = batch?.status === 'APPLYING'
    && Date.now() - new Date(batch.appliedAt ?? 0).getTime() < 15 * 60 * 1000;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-gradient text-white">
          <Replace className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold gradient-text">Renumbering</h1>
          <p className="text-xs text-slate-500">
            Un lot de renumérotation : on le dépose, on le simule, on relit ce qui partirait, puis on l'applique.
            L'écriture ne va que dans la mapping table — jamais dans public.address.
          </p>
        </div>
      </div>

      <GlassCard className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-800">Déposer un lot</p>
            <p className="mt-0.5 text-xs text-slate-500">
              Le fichier doit déjà être normalisé (CSV). Le dépôt ne lit rien du DMP : il range le fichier,
              ligne par ligne. Colonnes attendues : la rue, le numéro et le code postal actuels, plus au
              moins une colonne « nouvelle » (rue, numéro, extension ou boîte).
            </p>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onUpload(f); }}
          />
          <Button size="sm" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
            {busy === 'upload' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileUp className="mr-1.5 h-4 w-4" />}
            Choisir un fichier
          </Button>
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-slate-800">Lots déposés</p>
          <Button size="sm" variant="outline" onClick={() => void loadBatches()}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Actualiser
          </Button>
        </div>

        {batches === null && <Skeleton className="mt-3 h-24 w-full" />}
        {batches?.length === 0 && (
          <p className="mt-3 text-xs text-slate-500">Aucun lot déposé pour l'instant.</p>
        )}
        {batches && batches.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-3 font-semibold">Fichier</th>
                  <th className="py-2 pr-3 font-semibold">Déposé</th>
                  <th className="py-2 pr-3 font-semibold">Par</th>
                  <th className="py-2 pr-3 font-semibold">Lignes</th>
                  <th className="py-2 pr-3 font-semibold">État</th>
                  <th className="py-2 font-semibold">Verdicts</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setConfirming(false); void openBatch(b.id, null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setConfirming(false); void openBatch(b.id, null); } }}
                    className={cn(
                      'cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50/70',
                      detail?.batch.id === b.id && 'bg-brand-rose/[0.04]',
                    )}
                  >
                    <td className="py-2 pr-3 font-semibold text-slate-800">{b.filename}</td>
                    <td className="py-2 pr-3 text-slate-600">{fmt(b.createdAt)}</td>
                    <td className="py-2 pr-3 text-slate-500">{b.uploadedBy}</td>
                    <td className="py-2 pr-3 text-slate-700">{b.rowCount}</td>
                    <td className="py-2 pr-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                          b.status === 'APPLIED' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600',
                        )}
                      >
                        {STATUS_LABEL[b.status]}
                      </span>
                    </td>
                    <td className="py-2 text-slate-600">
                      {b.counts.pending === b.rowCount
                        ? '—'
                        : `${b.counts.resolved} résolue(s) · ${b.counts.alreadyDone} déjà faite(s) · ${b.counts.ambiguous + b.counts.unresolved + b.counts.unitMissing} à trancher`}
                      {b.counts.applied > 0 && ` · ${b.counts.applied} écrite(s)`}
                      {b.incompleteStreets > 0 && (
                        <span className="ml-1.5 font-semibold text-amber-600">· rue incomplète</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {detail && batch && (
        <GlassCard className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">{batch.filename}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {batch.rowCount} ligne(s) · déposé le {fmt(batch.createdAt)} par {batch.uploadedBy}
                {batch.simulatedAt && ` · simulé le ${fmt(batch.simulatedAt)}`}
                {batch.status === 'APPLIED' && ` · appliqué le ${fmt(batch.appliedAt)} par ${batch.appliedBy ?? '—'}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {batch.simulatedAt && (toSendBack > 0 || batch.incompleteStreets > 0) && (
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void onExport(batch)}>
                  {busy === 'export' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                  À renvoyer à Proximus (CSV)
                </Button>
              )}
              {batch.status !== 'APPLIED' && (
                <Button
                  size="sm"
                  variant={batch.simulatedAt ? 'outline' : 'default'}
                  disabled={busy !== null || applyingNow}
                  onClick={() => void onSimulate(batch.id)}
                >
                  {busy === 'simulate' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
                  {batch.simulatedAt ? 'Re-simuler' : 'Simuler'}
                </Button>
              )}
              {canApply && (
                <Button
                  size="sm"
                  disabled={busy !== null || confirming}
                  onClick={() => { if (filter !== null) void openBatch(batch.id, null); setConfirming(true); }}
                >
                  <PenLine className="mr-1.5 h-4 w-4" />
                  Appliquer {batch.counts.resolved} ligne(s)
                </Button>
              )}
            </div>
          </div>

          {/* Le récapitulatif AVANT l'écriture : ce qui part, où, et ce qui ne se rattrape pas. */}
          {confirming && canApply && (
            <div className="mt-4 rounded-lg border border-brand-rose/30 bg-brand-rose/[0.04] p-4">
              <p className="text-sm font-bold text-slate-800">Écrire dans le DMP de production ?</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-700">
                <li>
                  <strong>{batch.counts.resolved}</strong> déclaration(s) Proximus écrite(s) dans la{' '}
                  <strong>mapping table</strong>
                  {filter === null && (
                    <> : {batch.counts.resolved - overwrites} création(s), <strong>{overwrites} écrasement(s)</strong> d'une déclaration existante (l'ancienne valeur reste lisible ici et dans le journal d'actions)</>
                  )}
                  .
                </li>
                <li>public.address n'est pas touchée ; aucune adresse n'est créée ni supprimée.</li>
                <li>
                  Les {batch.counts.alreadyDone + batch.counts.ambiguous + batch.counts.unresolved + batch.counts.unitMissing} autre(s)
                  ligne(s) — déjà faites, à trancher, introuvables, boîtes absentes — ne partent pas.
                </li>
                {batch.incompleteStreets > 0 && (
                  <li className="text-amber-700">
                    Rue incomplète : {missingUnits} logement(s) en scope garderont l'ancien nom. C'est permis — à contester auprès de Proximus.
                  </li>
                )}
                <li>
                  La résolution est rejouée juste avant d'écrire : si le DMP a bougé depuis la simulation du{' '}
                  {fmt(batch.simulatedAt)}, rien ne part et le rapport est refait. Un lot appliqué ne se rejoue pas.
                </li>
              </ul>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" disabled={busy !== null} onClick={() => void onApply(batch)}>
                  {busy === 'apply' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />}
                  Confirmer l'écriture
                </Button>
                <Button size="sm" variant="outline" disabled={busy === 'apply'} onClick={() => setConfirming(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Rue incomplète (Anouk, 11-09) : informatif, pour contester le lot — l'écriture passe. */}
          {detail.warnings.length > 0 && (
            <div className="mt-4 space-y-2">
              {detail.warnings.map((w) => {
                const onNumbers = w.missing.filter((u) => u.houseInBatch).length;
                return (
                  <div key={`${w.postalCode}|${w.oldStreetName}`} className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                    <p className="flex items-start gap-1.5 text-xs font-semibold text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Rue incomplète — {w.oldStreetName} ({w.postalCode}) → {w.newStreetNames.join(' / ')} :{' '}
                        {w.missing.length} logement(s) en scope sur {w.inScope} absent(s) du lot
                        {onNumbers > 0 && `, dont ${onNumbers} sur un numéro présent dans le lot (autres boîtes)`}.
                      </span>
                    </p>
                    <details className="mt-1.5 pl-5">
                      <summary className="cursor-pointer text-[11px] text-amber-700">
                        Voir les logements — informatif, l'écriture reste permise
                      </summary>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {w.missing.map((u) => (
                          <span
                            key={u.jvid}
                            className={cn(
                              'rounded border px-1.5 py-0.5 font-mono text-[10px]',
                              u.houseInBatch ? 'border-amber-300 bg-white text-amber-800' : 'border-slate-200 bg-white text-slate-600',
                            )}
                            title={u.houseInBatch ? 'Numéro présent dans le lot, avec d\'autres boîtes' : 'Absent du lot'}
                          >
                            {addressText({ streetName: null, houseNumber: u.houseNumber, houseNumberExtension: u.houseNumberExtension, busNumber: u.busNumber, postalCode: null, city: null })}
                            {' · '}{u.jvid}
                          </span>
                        ))}
                      </div>
                    </details>
                  </div>
                );
              })}
            </div>
          )}

          {/* Les compteurs SONT le filtre : cliquer « Boîte absente » donne la pile à trancher. */}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void openBatch(batch.id, null)}
              className={cn(
                'rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
                filter === null ? 'border-brand-rose/40 bg-brand-rose/[0.06] text-brand-rose' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              Toutes ({batch.rowCount})
            </button>
            {COUNTERS.map(({ key, count }) => {
              const n = batch.counts[count];
              const style = key === 'PENDING' ? PENDING_STYLE : VERDICT_STYLE[key];
              return (
                <button
                  key={key}
                  type="button"
                  disabled={n === 0}
                  title={key === 'PENDING' ? 'Déposées, pas encore résolues contre le DMP.' : VERDICT_STYLE[key].help}
                  onClick={() => { setConfirming(false); void openBatch(batch.id, key); }}
                  className={cn(
                    'rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-40',
                    filter === key ? 'border-brand-rose/40 bg-brand-rose/[0.06] text-brand-rose' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {style.label} ({n})
                </button>
              );
            })}
            {batch.counts.applyFailed > 0 && (
              <button
                type="button"
                onClick={() => { setConfirming(false); void openBatch(batch.id, 'APPLY_FAILED'); }}
                className={cn(
                  'rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
                  filter === 'APPLY_FAILED' ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-rose-200 text-rose-600 hover:bg-rose-50',
                )}
              >
                Écriture en échec ({batch.counts.applyFailed})
              </button>
            )}
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-3 font-semibold">N°</th>
                  <th className="py-2 pr-3 font-semibold">Adresse actuelle</th>
                  <th className="py-2 pr-3 font-semibold">CP</th>
                  <th className="py-2 pr-3 font-semibold">Après</th>
                  <th className="py-2 pr-3 font-semibold">JVID</th>
                  <th className="py-2 pr-3 font-semibold">Verdict</th>
                  <th className="py-2 font-semibold">Pourquoi</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.rowNumber} className="border-b border-slate-50 last:border-0 align-top">
                    <td className="py-2 pr-3 text-slate-400">{l.rowNumber}</td>
                    <td className="py-2 pr-3 text-slate-700">{addressText(l.before)}</td>
                    <td className="py-2 pr-3 text-slate-500">{l.before.postalCode ?? '—'}</td>
                    <td className="py-2 pr-3">
                      {/* La cible réelle quand un logement est retenu ; sinon ce que le fichier demande, en gris. */}
                      <span
                        className={cn('inline-flex items-center gap-1.5', l.target ? 'text-slate-800' : 'text-slate-400')}
                        title={l.target ? 'Ce qui est écrit dans la mapping table' : 'Ce que le fichier demande — aucun logement retenu'}
                      >
                        <ArrowRight className={cn('h-3 w-3 shrink-0', l.target ? 'text-brand-rose' : 'text-slate-300')} />
                        {l.target ? addressText(l.target) : requestedText(l)}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-700">
                      {l.resolvedJvid ?? (l.candidates.length > 0 ? l.candidates.join(', ') : '—')}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-col items-start gap-1">
                        <VerdictPill verdict={l.verdict} />
                        {l.appliedAt && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" title={`Écrite le ${fmt(l.appliedAt)}`}>
                            <CheckCircle2 className="h-3 w-3" />
                            écrite
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 text-slate-500">
                      {l.reason ?? '—'}
                      {l.applyError && <p className="mt-1 font-semibold text-rose-600">{l.applyError}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detail.lines.length === 0 && (
              <p className="py-4 text-center text-xs text-slate-500">Aucune ligne dans ce filtre.</p>
            )}
          </div>

          <p className="mt-4 text-[10px] italic leading-snug text-slate-400">
            L'application n'écrit que les lignes « Résolue », dans la mapping table seulement, avec le verrou
            optimiste de chaque déclaration. Elle rejoue d'abord la résolution : si le DMP a bougé depuis la
            simulation, rien ne part. L'export liste les boîtes absentes, les adresses introuvables et les
            logements des rues incomplètes — la liste à renvoyer à Proximus.
          </p>
        </GlassCard>
      )}
    </div>
  );
}
